/** Extract scalar value from fetchRecords field object or plain value. */
export function getRecordFieldValue(record, fieldName) {
  if (!record || fieldName == null) {
    return null;
  }
  const field = record[fieldName];
  if (field == null) {
    return null;
  }
  if (typeof field === 'object' && !Array.isArray(field)) {
    if (field.value !== undefined && field.value !== null && field.value !== '') {
      return field.value;
    }
    if (
      field.displayValue !== undefined &&
      field.displayValue !== null &&
      field.displayValue !== ''
    ) {
      return field.displayValue;
    }
    // Do not fall back to field.label — that is the column label (e.g. "Total Floors"), not the cell value.
    return null;
  }
  return field;
}

/** Lookup label (displayValue) when present; otherwise scalar value. */
export function getRecordDisplayValue(record, fieldName) {
  if (!record || fieldName == null) {
    return null;
  }
  const field = record[fieldName];
  if (field && typeof field === 'object' && !Array.isArray(field)) {
    if (
      field.displayValue !== undefined &&
      field.displayValue !== null &&
      field.displayValue !== ''
    ) {
      return field.displayValue;
    }
  }
  return getRecordFieldValue(record, fieldName);
}

export function formatCurrencyInr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) {
    return '—';
  }
  return `₹${n.toLocaleString('en-IN')}`;
}

export function formatAreaSqft(area) {
  const n = Number(area);
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return `${n.toLocaleString('en-IN')} sq.ft`;
}

/** Integer counts from API; null/empty → 0 (never use field label as value). */
export function getRecordCountValue(record, fieldName) {
  const raw = getRecordFieldValue(record, fieldName);
  if (raw == null || raw === '') {
    return 0;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Map fetchRecords property row → UI model for Real Estate workspace. */
export function normalizePropertyRecord(record) {
  if (!record) {
    return null;
  }

  const id =
    getRecordFieldValue(record, 'id') ??
    (typeof record.id === 'object' ? record.id?.value : record.id);

  return {
    id,
    name: getRecordFieldValue(record, 'name') || 'Untitled',
    propertyCode: getRecordFieldValue(record, 'property_code'),
    propertyType: getRecordFieldValue(record, 'property_type'),
    ownershipType: getRecordFieldValue(record, 'ownership_type'),
    address: getRecordFieldValue(record, 'address'),
    city: getRecordFieldValue(record, 'city'),
    state: getRecordFieldValue(record, 'state'),
    country: getRecordFieldValue(record, 'country'),
    postalCode: getRecordFieldValue(record, 'postal_code'),
    status: getRecordFieldValue(record, 'status'),
    totalFloors: getRecordCountValue(record, 'total_floors'),
    totalUnits: getRecordCountValue(record, 'total_units'),
    description: getRecordFieldValue(record, 'description'),
    availableUnits: 0,
    occupiedUnits: 0,
    highDemandUnits: 0,
  };
}

export function normalizePropertyRecords(records) {
  if (!Array.isArray(records)) {
    return [];
  }
  return records.map(normalizePropertyRecord).filter(Boolean);
}

const CLOSED_INTEREST_STATUSES = new Set(['Lost', 'Closed', 'Converted']);
export const HIGH_DEMAND_THRESHOLD = 4;

export function recordId(record) {
  return getRecordFieldValue(record, 'id');
}

export function formatMonthlyRent(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) {
    return '';
  }
  if (n >= 1000 && n % 1000 === 0) {
    return `₹${n / 1000}K`;
  }
  return `₹${n.toLocaleString('en-IN')}`;
}

export function floorOrdinalLabel(floorNumber) {
  const n = Number(floorNumber);
  if (!Number.isFinite(n)) {
    return 'Floor';
  }
  if (n === 0) {
    return 'Ground';
  }
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) {
    return `${n}st`;
  }
  if (j === 2 && k !== 12) {
    return `${n}nd`;
  }
  if (j === 3 && k !== 13) {
    return `${n}rd`;
  }
  return `${n}th`;
}

export function unitVizStatus(unitStatus, openInterestCount) {
  const status = String(unitStatus || '').trim();
  if (status === 'Available') {
    return openInterestCount >= HIGH_DEMAND_THRESHOLD ? 'highDemand' : 'available';
  }
  return 'occupied';
}

function leadNameFromInterest(row) {
  const lookupName = getRecordDisplayValue(row, 'lead_id');
  if (lookupName && lookupName !== getRecordFieldValue(row, 'lead_id')) {
    return String(lookupName);
  }
  const rawName = getRecordFieldValue(row, 'name');
  if (rawName) {
    return String(rawName).split('—')[0].split('-')[0].trim();
  }
  return 'Lead';
}

function normalizeInterest(row) {
  const status = String(getRecordFieldValue(row, 'status') || '');
  const budgetMax = Number(getRecordFieldValue(row, 'budget_max')) || 0;
  const priority = String(getRecordFieldValue(row, 'priority') || '');
  return {
    id: recordId(row),
    unitId: getRecordFieldValue(row, 'unit_id'),
    leadId: getRecordFieldValue(row, 'lead_id'),
    leadName: leadNameFromInterest(row),
    budgetMax,
    budgetLabel: formatMonthlyRent(budgetMax) || formatCurrencyInr(budgetMax),
    priority,
    status,
    isOpen: !CLOSED_INTEREST_STATUSES.has(status),
  };
}

function groupInterestsByUnit(interestRows) {
  const byUnit = new Map();
  (interestRows || []).forEach(row => {
    const interest = normalizeInterest(row);
    if (!interest.unitId || !interest.isOpen) {
      return;
    }
    const list = byUnit.get(interest.unitId) || [];
    list.push(interest);
    byUnit.set(interest.unitId, list);
  });
  return byUnit;
}

function countOpenInterestsByUnit(interestsByUnit) {
  const counts = new Map();
  interestsByUnit.forEach((list, unitId) => {
    counts.set(unitId, list.length);
  });
  return counts;
}

function formatBandRange(min, maxExclusive) {
  if (maxExclusive === Infinity) {
    const label = formatMonthlyRent(min);
    return label ? `${label}+` : 'Above';
  }
  const maxLabel = formatMonthlyRent(maxExclusive - 1) || formatMonthlyRent(maxExclusive);
  const minLabel = formatMonthlyRent(min);
  return `${minLabel}–${maxLabel}`;
}

/** Rent-centered bands for demand vs price (residential vs commercial). */
export function getBudgetBands(monthlyRent) {
  const rent = Number(monthlyRent) || 0;
  if (rent <= 0) {
    return [
      { label: 'Low', min: 0, maxExclusive: Infinity, containsRent: true },
    ];
  }

  if (rent <= 40000) {
    return [
      {
        label: formatBandRange(rent - 7000, rent - 2000),
        min: Math.max(0, rent - 7000),
        maxExclusive: rent - 2000,
      },
      {
        label: formatBandRange(rent - 2000, rent),
        min: rent - 2000,
        maxExclusive: rent,
      },
      {
        label: formatBandRange(rent, rent + 3000),
        min: rent,
        maxExclusive: rent + 3000,
      },
      {
        label: formatBandRange(rent + 3000, Infinity),
        min: rent + 3000,
        maxExclusive: Infinity,
      },
    ].map(band => ({
      ...band,
      containsRent: rent >= band.min && rent < band.maxExclusive,
    }));
  }

  const step = rent >= 200000 ? 50000 : 25000;
  return [
    {
      label: formatBandRange(rent - 2 * step, rent - step),
      min: Math.max(0, rent - 2 * step),
      maxExclusive: rent - step,
    },
    {
      label: formatBandRange(rent - step, rent),
      min: rent - step,
      maxExclusive: rent,
    },
    {
      label: formatBandRange(rent, rent + step),
      min: rent,
      maxExclusive: rent + step,
    },
    {
      label: formatBandRange(rent + step, Infinity),
      min: rent + step,
      maxExclusive: Infinity,
    },
  ].map(band => ({
    ...band,
    containsRent: rent >= band.min && rent < band.maxExclusive,
  }));
}

function buildPricingInsight(rent, open, distribution) {
  if (!open.length) {
    return {
      message: 'No open leads on this unit yet — demand vs price will appear when interests are logged.',
      signal: 'neutral',
    };
  }

  const rentBand = distribution.find(b => b.containsRent);
  const atOrAboveRent = open.filter(i => Number(i.budgetMax) >= rent).length;
  const belowRent = open.length - atOrAboveRent;
  const rentBandCount = rentBand?.count || 0;
  const peakBand = distribution.reduce((best, band) =>
    band.count > best.count ? band : best
  );

  if (rentBandCount >= Math.ceil(open.length / 2)) {
    return {
      message: `${rentBandCount} of ${open.length} leads budget ${rentBand.label} — matches or exceeds list rent. Strong pricing signal.`,
      signal: 'positive',
    };
  }

  if (peakBand.containsRent && peakBand.count > 0) {
    return {
      message: `Most interest (${peakBand.count} leads) sits at ${peakBand.label}, aligned with list rent ₹${Number(rent).toLocaleString('en-IN')}.`,
      signal: 'positive',
    };
  }

  if (belowRent > atOrAboveRent) {
    return {
      message: `${belowRent} of ${open.length} leads budget below list rent. Consider a rent review or different lead targeting.`,
      signal: 'warning',
    };
  }

  if (atOrAboveRent > 0) {
    return {
      message: `${atOrAboveRent} of ${open.length} leads can pay at or above list rent — room to hold or test a higher price.`,
      signal: 'positive',
    };
  }

  return {
    message: `${open.length} leads interested — review budget distribution against list rent.`,
    signal: 'neutral',
  };
}

export function buildUnitDemand(unit, interests = []) {
  const rent = Number(unit?.monthlyRent) || 0;
  const open = (interests || []).filter(i => i.isOpen);
  const bands = getBudgetBands(rent).map(band => {
    const count = open.filter(
      i => i.budgetMax >= band.min && i.budgetMax < band.maxExclusive
    ).length;
    return { ...band, count };
  });
  const maxCount = Math.max(1, ...bands.map(b => b.count));
  const distribution = bands.map(band => ({
    ...band,
    percent: Math.round((band.count / maxCount) * 100),
  }));

  const priorityRank = { Hot: 1, High: 2, Medium: 3, Warm: 4, Low: 5 };
  const hotLeads = [...open].sort((a, b) => {
    const pa = priorityRank[a.priority] || 9;
    const pb = priorityRank[b.priority] || 9;
    if (pa !== pb) {
      return pa - pb;
    }
    return (b.budgetMax || 0) - (a.budgetMax || 0);
  });

  const insight = buildPricingInsight(rent, open, distribution);
  const atOrAboveRent = open.filter(i => Number(i.budgetMax) >= rent).length;

  return {
    leadCount: open.length,
    listRent: rent,
    atOrAboveRent,
    distribution,
    hotLeads,
    insight,
  };
}

function normalizeUnitForViz(row, interestCounts, interestsByUnit) {
  const id = recordId(row);
  const status = getRecordFieldValue(row, 'status');
  const openInterestCount = interestCounts.get(id) || 0;
  const area = getRecordFieldValue(row, 'area');
  return {
    id,
    name: getRecordFieldValue(row, 'name'),
    unitNumber: getRecordFieldValue(row, 'unit_number') || getRecordFieldValue(row, 'name'),
    floorId: getRecordFieldValue(row, 'floor_id'),
    unitType: getRecordFieldValue(row, 'unit_type'),
    area,
    areaLabel: formatAreaSqft(area),
    bedrooms: getRecordFieldValue(row, 'bedrooms'),
    bathrooms: getRecordFieldValue(row, 'bathrooms'),
    monthlyRent: getRecordFieldValue(row, 'monthly_rent'),
    rentLabel: formatMonthlyRent(getRecordFieldValue(row, 'monthly_rent')),
    rentDisplay: formatCurrencyInr(getRecordFieldValue(row, 'monthly_rent')),
    securityDeposit: getRecordFieldValue(row, 'security_deposit'),
    depositDisplay: formatCurrencyInr(getRecordFieldValue(row, 'security_deposit')),
    furnishing: getRecordFieldValue(row, 'furnishing'),
    status,
    openInterestCount,
    vizStatus: unitVizStatus(status, openInterestCount),
    interests: interestsByUnit.get(id) || [],
  };
}

function sortUnits(a, b) {
  return String(a.unitNumber || '').localeCompare(String(b.unitNumber || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * Group floors (top → bottom) with units and derived viz status.
 */
export function buildFloorVisualization({ floorRows = [], unitRows = [], interestRows = [] }) {
  const interestsByUnit = groupInterestsByUnit(interestRows);
  const interestCounts = countOpenInterestsByUnit(interestsByUnit);
  const units = unitRows
    .map(row => normalizeUnitForViz(row, interestCounts, interestsByUnit))
    .filter(u => u.id);

  const unitsByFloor = new Map();
  const unassigned = [];
  units.forEach(unit => {
    if (unit.floorId) {
      const list = unitsByFloor.get(unit.floorId) || [];
      list.push(unit);
      unitsByFloor.set(unit.floorId, list);
    } else {
      unassigned.push(unit);
    }
  });

  const floors = (floorRows || [])
    .map(row => {
      const id = recordId(row);
      const floorNumber = getRecordCountValue(row, 'floor_number');
      const floorLabel = floorOrdinalLabel(floorNumber);
      const floorName = getRecordFieldValue(row, 'name');
      const floorUnits = (unitsByFloor.get(id) || []).sort(sortUnits).map(unit => ({
        ...unit,
        floorLabel,
        floorName,
      }));
      return {
        id,
        name: floorName,
        floorNumber,
        label: floorLabel,
        units: floorUnits,
      };
    })
    .sort((a, b) => b.floorNumber - a.floorNumber);

  if (unassigned.length > 0) {
    floors.push({
      id: 'unassigned',
      name: 'Units',
      floorNumber: -1,
      label: floors.length === 0 ? 'Units' : 'Other',
      units: unassigned.sort(sortUnits).map(unit => ({
        ...unit,
        floorLabel: 'Units',
        floorName: 'Units',
      })),
    });
  }

  const stats = units.reduce(
    (acc, unit) => {
      acc[unit.vizStatus] = (acc[unit.vizStatus] || 0) + 1;
      return acc;
    },
    { available: 0, occupied: 0, highDemand: 0 }
  );

  return { floors, stats, totalUnits: units.length };
}

export const MEDIUM_DEMAND_MIN = 2;
export const MEDIUM_DEMAND_MAX = 3;

const INVENTORY_STATUS_ORDER = ['Available', 'Reserved', 'Occupied'];
const INVENTORY_COLORS = {
  Available: '#2e844a',
  Reserved: '#ffb75d',
  Occupied: '#706e6b',
};

export function classifyUnitDemandTier(status, openCount) {
  if (String(status || '').trim() !== 'Available') {
    return null;
  }
  const count = Number(openCount) || 0;
  if (count >= HIGH_DEMAND_THRESHOLD) {
    return 'high';
  }
  if (count >= MEDIUM_DEMAND_MIN && count <= MEDIUM_DEMAND_MAX) {
    return 'medium';
  }
  if (count >= 1) {
    return 'low';
  }
  return null;
}

function buildInterestsByUnit(interestRows, propertyIds) {
  const interestsByUnit = new Map();
  let activeLeads = 0;
  (interestRows || []).forEach(row => {
    const propertyId = getRecordFieldValue(row, 'property_id');
    if (!propertyIds.has(propertyId)) {
      return;
    }
    const interest = normalizeInterest(row);
    if (!interest.isOpen) {
      return;
    }
    activeLeads += 1;
    if (!interest.unitId) {
      return;
    }
    const list = interestsByUnit.get(interest.unitId) || [];
    list.push(interest);
    interestsByUnit.set(interest.unitId, list);
  });
  return { interestsByUnit, activeLeads };
}

function buildInventoryDemandStats(unitRows, interestsByUnit, propertyIds) {
  const inventoryCounts = { Available: 0, Reserved: 0, Occupied: 0 };
  let highDemand = 0;
  let mediumDemand = 0;
  let lowDemand = 0;

  (unitRows || []).forEach(row => {
    const propertyId = getRecordFieldValue(row, 'property_id');
    if (!propertyIds.has(propertyId)) {
      return;
    }
    const status = String(getRecordFieldValue(row, 'status') || '').trim();
    if (status === 'Available') {
      inventoryCounts.Available += 1;
    } else if (status === 'Reserved') {
      inventoryCounts.Reserved += 1;
    } else if (status === 'Occupied') {
      inventoryCounts.Occupied += 1;
    }

    if (status !== 'Available') {
      return;
    }

    const openCount = (interestsByUnit.get(recordId(row)) || []).length;
    const tier = classifyUnitDemandTier(status, openCount);
    if (tier === 'high') {
      highDemand += 1;
    } else if (tier === 'medium') {
      mediumDemand += 1;
    } else if (tier === 'low') {
      lowDemand += 1;
    }
  });

  const totalUnits =
    inventoryCounts.Available + inventoryCounts.Reserved + inventoryCounts.Occupied;
  const occupancyPct =
    totalUnits > 0
      ? Math.round((inventoryCounts.Occupied / totalUnits) * 100)
      : 0;

  const inventoryMax = Math.max(1, ...INVENTORY_STATUS_ORDER.map(k => inventoryCounts[k]));
  const inventory = INVENTORY_STATUS_ORDER.map(key => ({
    key,
    label: key,
    count: inventoryCounts[key],
    color: INVENTORY_COLORS[key],
    percent: Math.round((inventoryCounts[key] / inventoryMax) * 100),
    drillDown: { kind: 'status', value: key },
  }));

  return {
    totalUnits,
    occupancyPct,
    inventory,
    demand: [
      {
        tier: 'high',
        label: 'High Demand',
        count: highDemand,
        suffix: '🔥',
        drillDown: { kind: 'demand', value: 'high' },
      },
      {
        tier: 'medium',
        label: 'Medium Demand',
        count: mediumDemand,
        drillDown: { kind: 'demand', value: 'medium' },
      },
      {
        tier: 'low',
        label: 'Low Demand',
        count: lowDemand,
        drillDown: { kind: 'demand', value: 'low' },
      },
    ],
  };
}

/** Aggregate portfolio KPIs from fetchRecords list payloads. */
export function buildPortfolioStats({ propertyRows, unitRows, interestRows }) {
  const propertyIds = new Set(
    (propertyRows || []).map(recordId).filter(Boolean)
  );
  const { interestsByUnit, activeLeads } = buildInterestsByUnit(
    interestRows,
    propertyIds
  );
  const stats = buildInventoryDemandStats(unitRows, interestsByUnit, propertyIds);

  return {
    propertyCount: propertyIds.size,
    activeLeads,
    ...stats,
  };
}

/** Single-property inventory + demand rollup. */
export function buildPropertyStats({ propertyId, unitRows, interestRows }) {
  const propertyIds = new Set([propertyId].filter(Boolean));
  const { interestsByUnit, activeLeads } = buildInterestsByUnit(
    interestRows,
    propertyIds
  );
  const stats = buildInventoryDemandStats(unitRows, interestsByUnit, propertyIds);

  return {
    activeLeads,
    ...stats,
  };
}

export function getDrillDownLabel(drillDown, { propertyName } = {}) {
  if (!drillDown) {
    return { title: 'Units', subtitle: '' };
  }
  const scope = propertyName ? propertyName : 'Portfolio';
  if (drillDown.kind === 'status') {
    return {
      title: `${drillDown.value} units`,
      subtitle: scope,
    };
  }
  const tierLabels = {
    high: 'High demand units',
    medium: 'Medium demand units',
    low: 'Low demand units',
  };
  return {
    title: tierLabels[drillDown.value] || 'Units',
    subtitle: scope,
  };
}

export function buildUnitDrillDownRows({
  unitRows,
  interestRows,
  propertyIds,
  propertyNameById = {},
}) {
  const { interestsByUnit } = buildInterestsByUnit(interestRows, propertyIds);

  return (unitRows || [])
    .filter(row => propertyIds.has(getRecordFieldValue(row, 'property_id')))
    .map(row => {
      const id = recordId(row);
      const propertyId = getRecordFieldValue(row, 'property_id');
      const status = String(getRecordFieldValue(row, 'status') || '').trim();
      const openInterestCount = (interestsByUnit.get(id) || []).length;
      const rent = Number(getRecordFieldValue(row, 'monthly_rent')) || 0;
      return {
        id,
        propertyId,
        propertyName: propertyNameById[propertyId] || 'Property',
        name: getRecordFieldValue(row, 'name'),
        unitNumber: getRecordFieldValue(row, 'unit_number') || getRecordFieldValue(row, 'name'),
        unitType: getRecordFieldValue(row, 'unit_type'),
        status,
        rent,
        rentDisplay: formatMonthlyRent(rent) || formatCurrencyInr(rent),
        openInterestCount,
        demandTier: classifyUnitDemandTier(status, openInterestCount),
      };
    })
    .sort((a, b) => {
      const nameCmp = String(a.propertyName).localeCompare(String(b.propertyName));
      if (nameCmp !== 0) {
        return nameCmp;
      }
      return String(a.unitNumber).localeCompare(String(b.unitNumber), undefined, {
        numeric: true,
      });
    });
}

export function filterUnitDrillDownRows(rows, drillDown) {
  if (!drillDown) {
    return rows || [];
  }
  if (drillDown.kind === 'status') {
    return (rows || []).filter(row => row.status === drillDown.value);
  }
  if (drillDown.kind === 'demand') {
    return (rows || []).filter(row => row.demandTier === drillDown.value);
  }
  return rows || [];
}
